import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ResourceType } from '@app/core/model/resource-type';
import { IWorkgroup } from '@app/core/model/workgroup';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { NgxSmartModalComponent, NgxSmartModalService } from '@bssh/comp-lib';
import _ from 'lodash';
import { merge, of } from 'rxjs';
import { delay, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { StringUtilities } from '@app/core/utilities/string-utilities';

interface OAuthDevicePostRequest {
  Code: string;
  WorkgroupId?: string;
  IFrame: boolean;
  UserAction: 'Cancel' | 'Grant';
}

interface ScopeItem {
  ResourceType: ResourceType,
  ResourceName: string;
  Permission: string;
}

interface OAuthDeviceResponse {
  ScopeItems: ScopeItem[];
  Code: string;
  AppName: string;
  ActingUserId: string;
  CompanyName: string;
  LogoUrl: string;
  AgreementIdEula?: number;
  AgreementIdPrivacy?: number;
  IFrame: boolean;
  Workgroup?: IWorkgroup;
}

enum DeviceFlowState {
  INITIAL = 'INITIAL',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}


@Component({
  selector: 'app-oauth-device-modal',
  templateUrl: './oauth-device-modal.component.html',
  styleUrls: ['./oauth-device-modal.component.scss']
})
export class OAuthDeviceModalComponent extends BaseModalComponent implements AfterViewInit, OnInit {
  public modalType = 'oAuthDeviceModal';
  public modalTitle = '';

  get confirmButtonText(): string {
    return this.deviceFlowState === DeviceFlowState.INITIAL ? 'Accept' : 'Submit';
  }

  get oAuthCloseButtonText(): string {
    return this.deviceFlowState === DeviceFlowState.INITIAL ? `Don't Accept` : 'Close';
  }

  get oAuthShowCloseButton(): boolean {
    return this.deviceFlowState === DeviceFlowState.INITIAL || this.deviceFlowState === DeviceFlowState.SUCCESS;
  }

  get oAuthShowConfirmButton(): boolean {
    return this.deviceFlowState === DeviceFlowState.INITIAL || this.deviceFlowState === DeviceFlowState.ERROR
  }

  deviceFlowState: DeviceFlowState = DeviceFlowState.INITIAL;

  public deviceCodeControl: FormControl = new FormControl('');

  public alertMessages: string[];
  public errorMsg: string;

  get DeviceFlowState() {
    return DeviceFlowState;
  }

  public response: OAuthDeviceResponse;

  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    super(ngxSmartModalService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.closable = false;
  }

  ngAfterViewInit(): void {
    this.loadInitialState();

    this.subs.sink = merge(
      this.ngxSmartModalService.getModal(this.modalType).onConfirm.pipe(map(() => 'Grant')),
      this.ngxSmartModalService.getModal(this.modalType).onClose.pipe(map(() => 'Cancel'))
    ).pipe(
      // Deal with all side effects and device flow state switching in tap operator
      map((userAction) => {
        if(this.deviceFlowState === DeviceFlowState.SUCCESS) {
          this.router.navigateByUrl('/');
          return null;
        } else if(userAction === 'Grant' && this.deviceFlowState === DeviceFlowState.ERROR) {
          this.router.navigate([], {
            queryParams: { code: this.deviceCodeControl.value },
            queryParamsHandling: 'merge', // Merge with existing query params
          });

          // Unfortunately necessary to clear event queue so new query params are used in reloaded state
          setTimeout(() => {
            this.loadInitialState();
          }, 0);
          return null;
        }
        
        return userAction;
      }),
      tap(() => this.cdr.detectChanges()),
      filter((userAction) => {
        return !!userAction
      }),
      switchMap((userAction: 'Grant' | 'Cancel') => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });
        return this.http.post(BsApiEndPoints.OAUTH_DEVICE, this.getOAuthDevicePostRequest(userAction), { headers }).pipe(
          // In MVC there's a different behavior for cancelling where we display to the user the "success" modal (which seems incorrect but not fixing that).
          //  We're directing user to dashboard instead because I cannot figure out how to get the modal to not close when a user hits the close button 
          // or reopen the modal after it has been closed. None of the close functionality in the smart modal works as expected. 
          // We can fix this when this is migrated to rad and UX is redesigned for oauth modals
          tap(() => {
            if(userAction === 'Cancel') {
              this.router.navigateByUrl('/');
            }
          })
        )
      })
      ).subscribe(
      {
        next: () => {
          this.deviceFlowState = DeviceFlowState.SUCCESS;
          this.cdr.detectChanges();
        },
        error: (response) => {
          this.errorMsg = response.error.ErrorMessage;
        }
      });
    
  }

  loadInitialState(): void {
    const pascaledQueryParams = window.location.search.slice(1).split('&').map((queryParam) => {
      let [ key, value ] = queryParam.split('=');

      return [StringUtilities.snakeToPascal(key), value].join('=');
    }).join('&');

    this.subs.sink = this.http.get(`${BsApiEndPoints.OAUTH_DEVICE}?${pascaledQueryParams}`).pipe(
      tap((response: OAuthDeviceResponse) => {
        this.deviceFlowState = DeviceFlowState.INITIAL;  
        this.errorMsg = '';
        this.response = response;
      }),
      // Need to add this to get modal to resize
      delay(0),
      finalize(() => {
        this.ngxSmartModalService.setModalData({ }, this.modalType);
        this.ngxSmartModalService.open(this.modalType);
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      })
    )
    .subscribe({
      error: (response: HttpErrorResponse) => {
          this.deviceFlowState = DeviceFlowState.ERROR;
          this.errorMsg = response.error.ErrorMessage;
          this.response = response.error.AdditionalData;
      }
    });
  }

  getOAuthDevicePostRequest(userAction: 'Grant' | 'Cancel'): OAuthDevicePostRequest {
    let code;
    if(this.deviceFlowState === DeviceFlowState.ERROR) {
      code = this.deviceCodeControl.value;
    } else {
      code = this.response.Code
    }

    return {
      Code: code,
      UserAction: userAction,
      IFrame: this.response.IFrame
    }
  }

  getAgreementUrl(agreementId: number | string): string {
    return `/agreements/view?agreementid=${agreementId}`;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

}
